import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CardWithHeader } from "@/components/card-with-header";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "@/components/ui/spinner";
import { VolumeApi } from "@/lib/volume-api";
import { getMessageFromError } from "@/lib/api-errors";

export const Route = createFileRoute(
  "/(app)/_main-layout/_in-store/in-store/agents_/$agentId",
)({
  component: AgentDetailLayout,
  beforeLoad: ({ location, params, search }) => {
    const base = `/in-store/agents/${params.agentId}`;
    if (location.pathname === base || location.pathname === `${base}/`) {
      throw redirect({
        to: "/in-store/agents/$agentId/config",
        params: { agentId: params.agentId },
        search: (search as { applicationId?: string }) ?? {},
      });
    }
  },
});

function AgentDetailLayout() {
  const context = Route.useRouteContext();
  const { agentId } = Route.useParams();

  const agentQuery = useQuery({
    queryKey: ["agent", agentId, context.selectedApplicationId],
    queryFn: () => {
      const api = new VolumeApi(
        context.accessToken,
        context.selectedApplicationId,
      );
      return api.getAgent({ agentId });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link
          to="/in-store/agents"
          search={(s: { applicationId?: string }) => ({
            applicationId: s?.applicationId,
          })}
          className="hover:underline"
        >
          Agents
        </Link>
        <span>/</span>
        <span className="text-slate-700">
          {agentQuery.data?.agentId ?? agentId}
        </span>
      </div>

      <CardWithHeader
        variant="exchange"
        label="Agent"
        title={agentQuery.data ? `Agent ${agentQuery.data.agentId}` : "Agent"}
        subtitle={agentQuery.data?.location}
      >
        {agentQuery.isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        ) : agentQuery.isError ? (
          <div className="py-4 text-sm text-red-600">
            Failed to load agent: {getMessageFromError(agentQuery.error)}
          </div>
        ) : agentQuery.data ? (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Agent ID" value={agentQuery.data.agentId} mono />
            <Field label="Agent Code" value={agentQuery.data.agentCode} mono />
            <Field label="Location" value={agentQuery.data.location} />
            <Field
              label="Tag Serial Number"
              value={agentQuery.data.tagSerialNumber ?? "—"}
              mono
            />
            <Field
              label="Status"
              value={
                agentQuery.data.disabled ? (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    Disabled
                  </span>
                ) : (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                    Enabled
                  </span>
                )
              }
            />
            <Field
              label="Created"
              value={new Date(agentQuery.data.createdAt).toLocaleString()}
            />
          </dl>
        ) : null}
      </CardWithHeader>

      <Tabs value="config">
        <TabsList>
          <Link
            to="/in-store/agents/$agentId/config"
            params={{ agentId }}
            search={(s: { applicationId?: string }) => ({
              applicationId: s?.applicationId,
            })}
          >
            <TabsTrigger value="config">Agent Config</TabsTrigger>
          </Link>
        </TabsList>
      </Tabs>

      <div className="mt-2">
        <Outlet />
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd
        className={`mt-1 text-sm text-slate-800 ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}
